<script>
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { componentStore } from "@common/componentStore/componentStore.js";
import { library } from "@common/library/library";
import { recordStore } from "@common/recordStore";
import { TooltipPlugin } from '@syncfusion/ej2-vue-popups';
import { GenerateComponent } from "@common/activityStepPanel/GenerateComponent";
import Icon from '@common/icon/Icon.vue';

const editorSettings = {};

const componentPreviewConfig = {
    cId: "componentPreview",
    cType: "componentPreview",
    cTitle: "Component Preview",
    cTemplate: `<div class="component-preview" :data-test-id="testStoreId">
    <div class="component-preview-enable" v-if="!previewEnabled">
        <control name="ENABLE_PREVIEW"></control>
    </div>
    <div v-if="previewEnabled" :class="{'position-indicators-enabled': enablePositionIndicators}">
        <div v-if="selectedComponent" class="component-preview-header">
            <div class="component-preview-info">
                Title: {{selectedComponent.cTitle}}<br/>
                cId: {{componentId}}<br/>
                <fieldset v-if="jiraTicketInfo">
                    <legend>JIRA Ticket Information</legend>
                    URL: <a :href="jiraTicketInfo.url" target="_blank">{{jiraTicketInfo.title}}</a><br/>
                    ID: {{jiraTicketInfo.ID}}<br/>
                    Project: {{jiraTicketInfo.project}}<br/>
                    Sprints: {{jiraTicketInfo.sprints}}<br/>
                </fieldset>
            </div>
            <div class="component-preview-controls">
                <control name="DISABLE_PREVIEW" v-if="previewEnabled"></control>
                <control name="RELOAD_PREVIEW"></control>
                <control v-if="componentsPassed" name="PREV_COMPONENT"></control>
                <control v-if="componentsPassed" name="NEXT_COMPONENT"></control>
            </div>
        </div>
        <div v-if="!selectedComponent && !previewReloading">
            No component to preview.
        </div>
        <div v-if="!selectedComponent && previewReloading">
            Loading component preview...
        </div>
        <div v-if="selectedComponent && !previewReloading" class="component-description__outer-div">
            <div v-if="selectedComponent.pages" class="component-preview-area">
                <component :is="selectedComponent.cType"
                    :unique-store-id="testStoreId"
                    :pageName="currentPage"
                    :key="$route.fullPath"
                    :editor-enabled="enableEditorInPreview"
                    :prop-test-mode="testMode"></component>
            </div>
            <div v-else class="component-preview-area">
                <component
                        :is="selectedComponent.cType"
                        :is-parent="true"
                        :unique-store-id="testStoreId"
                        :cId="selectedComponent.cId"
                        :pcId="selectedComponent.cId"
                        v-bind="mappedProps"
                        :editor-enabled="enableEditorInPreview"
                        :prop-test-mode="testMode"
                        :context-vol-id="volId"
                        :context-opp-id="oppId"
                        :context-org-id="orgId"
                        :context-lbe-id="lbeId"
                ></component>
            </div>
            <div :ref="'infoContainer_' + selectedComponent.cId"
                 class="component-description__component-info"
                 v-if="ecUserProperty?.CLIENT_ROLE_OPTION || isDevDash">
                <div class="component-description__info-icon"
                     @click="toggleExpansion(); showComponentInfo = !showComponentInfo;"
                     v-if="selectedComponent && !previewReloading">
                    <Icon name="info" color="#484848" size="xl"/>
                </div>
                <div class="component-information"
                     :class="{ expanded: showComponentInfo }"
                     @click.stop>
                    <h3>Component Information:</h3>
                    <div class="component-information__description" v-if="selectedComponent.cInfo">
                        <span>Component Description: </span>
                        <div>
                            <component :is="componentInformation.cId" :key="selectedComponent.cId"></component>
                         </div>
                    </div>
                    <div class="component-information__extra">
                        <div class="info-row">
                            <span>cId:&ensp;</span>
                            <span class="info-row">
                                <span :title="selectedComponent.cId">{{selectedComponent.cId}}</span>
                                <div class="copy-wrapper">
                                    <transition name="fade" mode="out-in">
                                        <Icon
                                                v-if="copiedId !== selectedComponent.cId"
                                                key="copy"
                                                name="copy"
                                                size="xl"
                                                @click="copyText(selectedComponent.cId)"></Icon>
                                        <Icon
                                                v-else
                                                key="checkmark"
                                                name="checkmark"
                                                size="2xl"
                                                color="#22c55e"></Icon>
                                    </transition>
                                </div>
                            </span>
                        </div>
                        <div><span>Created by:&ensp;</span>{{selectedComponent.cCreatedBy}}</div>
                        <div><span>Last modified:&ensp;</span>{{selectedComponent.cLastModify}}</div>
                        <div><span>Last modified by:&ensp;</span>{{selectedComponent.cModifiedBy}}</div>
                        <div v-if="selectedComponent?.datasets?.length"
                            v-for="dataset in selectedComponent.datasets"
                            :key="dataset"
                             class="dataset-list">
                             <h3>Datasets Used:</h3>
                            <ul></ul>
                            <li class="info-row">
                                <span>{{dataset}}</span>
                                <div class="copy-wrapper">
                                    <transition name="fade" mode="out-in">
                                        <Icon
                                                v-if="copiedId !== dataset"
                                                key="copy"
                                                name="copy"
                                                size="xl"
                                                @click="copyText(dataset)"></Icon>
                                        <Icon
                                                v-else
                                                key="checkmark"
                                                name="checkmark"
                                                size="2xl"
                                                color="#22c55e"></Icon>
                                    </transition>
                                </div>
                            </li>
                        </div>
                        <div v-if="selectedComponent?.positions?.length">
                            <h3>Layout Information:</h3>
                            <div v-for="section in layoutComponents"
                                 :key="section.key">
                                 <div></div>
                                <ul>{{section.label}}</ul>
                                <li
                                        v-for="component in section.components"
                                        :key="component.id || component.cId"
                                        class="info-row"
                                >
                                    <span>{{ component.cId }}</span>
                                    <div class="copy-wrapper">
                                        <transition name="fade" mode="out-in">
                                            <Icon
                                                    v-if="copiedId !== component.cId"
                                                    key="copy"
                                                    name="copy"
                                                    size="xl"
                                                    @click="copyText(component.cId)"></Icon>
                                            <Icon
                                                    v-else
                                                    key="checkmark"
                                                    name="checkmark"
                                                    size="2xl"
                                                    color="#22c55e"></Icon>
                                        </transition>
                                    </div>
                                </li>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>`,
    controls: [
        {
            name: 'ENABLE_PREVIEW',
            type: 'button',
            label: 'Enable Preview',
            onclick: 'togglePreview'
        },
        {
            name: 'DISABLE_PREVIEW',
            type: 'button',
            label: 'Disable Preview',
            onclick: 'togglePreview'
        },
        {
            name: 'RELOAD_PREVIEW',
            type: 'button',
            label: 'Reload',
            onclick: 'reloadComponent',
            cssClass: "e-block e-info",
        },
        {
            name: 'PREV_COMPONENT',
            type: 'button',
            label: 'Previous',
            onclick: 'prevComponent',
            cssClass: "e-block e-info",
        },
        {
            name: 'NEXT_COMPONENT',
            type: "button",
            label: "Next",
            onclick: "nextComponent",
            cssClass: "e-block e-info",
        }
    ],
    layoutDatamap: [
        {
            "property": "currentComponentId", "from": "componentPreview", "to": "parentStore", "as": "selectedComponentId"
        }
    ]
};

let componentPreviewCmp = {
    editorSettings: editorSettings,
    name: 'componentPreview',
    props: {
        componentId: {
            default: false
        },
        components: {
            default: {}
        },
        testStoreId: {
            type: String,
            default: 'devdash_test_window'
        },
        enableEditorInPreview: {
            type: Boolean,
            default: false
        },
        enablePositionIndicators: {
            type: Boolean,
            default: false
        }
    },
    globalMethods: ['selectComponent'],
    data() {
        return {
            componentConfig: undefined,
            currentComponentId: false,
            mappedProps: {},
            previewEnabled: false,
            previewReloading: false,
            showComponentInfo: false,
            showMore: false,
            copiedId: null
        };
    },
    computed: {
        currentPage() {
            return this.$route.fullPath.substring(1);
        },
        selectedComponent() {
            if (!this.previewEnabled || !this.componentId) return null;
            return this.componentConfig;
        },
        componentsPassed() {
            let componentsPassed = true;
            if ((Array.isArray(this.components) && !this.components.length) || (library.objIsEmpty(this.components))) {
                componentsPassed = false;
            }
            return componentsPassed;
        },
        jiraTicketInfo() {
            return errorHandler.get('jiraTicketInfo');
        },
        componentInformation() {
            return new GenerateComponent(this, this?.selectedComponent?.cInfo, this?.selectedComponent?.cId);
        },
        layoutComponents() {
            const positions = this?.selectedComponent?.positions || [];
            return positions.filter((position) => position.components?.length)
                .map((position) => ({
                    label: position.label,
                    key: library.camelCase(position.label),
                    components: position.components || []
                }));
        }
    },
    watch: {
        componentId: async function () {
            this.previewReloading = true;
            this.updateState().then(() => {
                this.previewReloading = false;
            });
        }
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
    },
    methods: {
        selectComponent(component) {
            this.componentId = component.cId;
            this.updateState();
        },
        copyText(text) {
            library.copyTextToClipboard(text);
            this.copiedId = text;
            clearTimeout(this.copyTimeout);
            this.copyTimeout = setTimeout(() => {
                this.copiedId = null;
            }, 1500);
        },
        async updateState() {
            if (!this.componentId) {
                // if (this.componentConfig) this.componentConfig = undefined;
                return;
            }
            let cId = this.componentId;
            let tmpConfig = await componentStore.getConfig(cId);
            const config = library.cloneObj(tmpConfig);
            if (config.cType !== 'layoutContainer') {
                config.cId = cId;
            }
            config.loaded = true;
            this.componentConfig = library.cloneObj(config);
            // Load Test Data
            // TODO: move this to a separate library function
            let cType = this.componentConfig.cType;
            if (typeof testData === 'undefined' || typeof testData[cType] === 'undefined') return;
            this.mappedProps = {};
            let tmpState = {};
            for (const i in testData[cType]) {
                if (i === 'length') continue;
                let propName = 'prop' + library.capitalizeFirstLetter(i);
                if (i === 'cId' || i === 'config') propName = i;
                this.mappedProps[propName] = testData[cType][i][cType];
                tmpState[i] = testData[cType][i][cType];
            }
            if (this.previewEnabled) {
                if (this.layoutId) {
                    let interval = setInterval(() => {
                        if (typeof this.$store.state[this.layoutId][this.componentId] !== 'undefined') {
                            clearInterval(interval);
                            library.mergeObj(this.$store.state[this.layoutId][this.componentId], tmpState);
                        }
                    }, 500); // NOSONAR
                } else {
                    let interval = setInterval(() => {
                        if (typeof this.$store.state[this.layoutId][this.componentId] !== 'undefined') {
                            clearInterval(interval);
                            library.mergeObj(this.$store.state[this.componentId], tmpState);
                        }
                    }, 500); // NOSONAR
                }
            }
        },
        reloadComponent: async function () {
            this.previewReloading = true;
            recordStore().renewSubscriptions({ force: true });
            setTimeout(() => {
                this.previewReloading = false;
            }, 500);
        },
        prevComponent: function () {
            let components = [];
            for (let i in this.components) {
                if (i === 'length') continue;
                components.push(this.components[i].cId);
            }
            if (!components.length) return;
            const count = components.length - 1;
            const selectedcId = this.componentId;
            const index = components.indexOf(selectedcId) ? components.indexOf(selectedcId) - 1 : count;
            const prevcId = components[index];
            this.previewReloading = true;
            this.currentComponentId = prevcId;
            this.$nextTick(() => {
                this.previewReloading = false;
            });
        },
        nextComponent: function () {
            let components = [];
            for (let i in this.components) {
                if (i === 'length') continue;
                components.push(this.components[i].cId);
            }
            if (!components.length) return;
            const count = components.length - 1;
            const tmpcId = this.componentId;
            const index = components.indexOf(tmpcId) <= (count - 1) ? components.indexOf(tmpcId) + 1 : 0;
            const nextcId = components[index];
            this.previewReloading = true;
            this.currentComponentId = nextcId;
            this.$nextTick(() => {
                this.previewReloading = false;
            });
        },
        togglePreview: function () {
            this.previewEnabled = !this.previewEnabled;
            this.updateState();
        },
        toggleExpansion: function () {
            const infoBox = document?.querySelector('.component-information');
            if (infoBox && !infoBox.classList.contains('expanded')) infoBox.classList.add('expanded');
            else {
                infoBox.classList.remove('expanded');
            }
        },
        async onCreate() {
            await this.updateState();
        },
        handleClickOutside(e) {
            const infoContainer = `infoContainer_${this?.selectedComponent?.cId}`;
            const container = this.$refs[infoContainer];
            if (container && !container.contains(e.target)) this.showComponentInfo = false;
        }
    },
    components: {
        "ejs-button": defineAsyncComponent(() => import('@common/syncfusion/button')),
        "ejs-tooltip": TooltipPlugin,
        Icon
    }
};
componentStore.initComponent(componentPreviewCmp, componentPreviewConfig);
export default componentPreviewCmp;
</script>

<style scoped>
    .position-indicators-enabled :deep(.dynamic-position-identifier) {
        display: block;
    }
    .component-preview .component-preview-header {
        display: flex;
    }
    .component-preview .component-preview-info {
        font-size: 16px;
        flex-grow: 3;
    }
    .component-preview .component-preview-controls {
        margin-bottom: 10px;
        text-align: right;
        flex-grow: 7;
    }
    .component-preview .component-preview-controls > div {
        flex-grow: 3;
    }
    .component-preview  .component-preview-controls .control {
        width: 180px;
        display: inline-block;
    }

    .component-preview  .component-preview-enable {
        text-align: center;
        width: 300px;
        padding-bottom: 10px;
        margin: 100px auto auto auto;
    }
    .component-description__outer-div {
        position: relative;
    }
    .component-preview .component-preview-area {
        border: 5px solid #FF1493;
        min-height: 300px;
        position: relative;
        margin: 10px;
        padding: 5px;
    }
    .component-description__info-icon {
        border-radius: 50%;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 1rem;
        aspect-ratio: 1 / 1;
        position: absolute;
        top: 0.75rem;
        right: 1.5rem;
        z-index: 9999;
    }
    :deep(.component-description__info-icon i) {
        background: #fff;
        border-radius: 50%;
    }
    .component-information {
        position: absolute;
        top: 2.25rem;
        right: 2.25rem;

        width: 350px;
        max-width: 350px;
        max-height: 50vh;
        overflow-y: scroll;

        background: #fff;
        border: 1px solid #d0d0d0;
        box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        padding: 0 1.5rem 2rem;

        transform: scale(0.8) translate(10px, -10px);
        transform-origin: top right;
        opacity: 0;

        transition:
                transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1),
                opacity 0.2s ease;

        pointer-events: none;
        word-break: break-word;

        scrollbar-width: thin; /* Firefox */

    }
    .component-information::-webkit-scrollbar {
        width: 6px;
    }
    .component-information.expanded {
        transform: scale(1) translate(0, 0);
        opacity: 1;
        pointer-events: auto;
    }
    :deep(.component-description__component-info h3) {
        cursor: default;
        margin-bottom: 0.25rem;
    }
    .component-information button {
        background: #efefef;
        color: #6b6b6b;
        border: 1px solid #9b9b9b;
        border-radius: 0;
        margin: 1rem;
        padding: 0.25rem 0.75rem;
    }
    .component-information .component-information__button_div {
        position: absolute;
        right: 0;
        bottom: 0;
    }
    .component-information__description {
        position: relative;
        padding-bottom: 1rem;
        top: 1rem;
    }
    .component-information__description > div {
        border: 1px solid #888888;
        padding: 1rem;
    }
    :deep(.component-information__description > span) {
        background: #fff;
        position: absolute;
        top: -9px;
        left: 8px;
        padding: 0 4px;
    }
    .info-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.25rem 0;
    }
    .info-row span {
        flex: 1;
        min-width: 0; /* IMPORTANT for ellipsis in flex */
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .copy-wrapper {
        flex-shrink: 0;
    }
    :deep(.component-information ol) {
        margin: 0;
        padding-inline-start: 0;
        text-decoration: underline;
    }
    :deep(.component-information ul li) {
        list-style: none;
    }
    :deep(.component-information ol li) {
        background: blue;
    }
    :deep(.component-information li),
    .info-row li {
        padding-left: 1rem;
    }

    :deep(.component-information i) {
        cursor: pointer;
        font-size: 1.25rem;
        padding-left: 0.5rem;
    }
    :deep(.component-preview-area i) {
        background: #fff;
        border-radius: 50%;
        cursor: pointer;
        font-size: 1.25rem;
        padding: 0.35rem;
    }
    :deep(.component-preview-area i:hover),
    :deep(.component-preview-area i:active),
    :deep(.component-preview-area i:focus) {
        background: #ededed;
    }
</style>
